<script setup>
// "Testando como" da tela Testar (Fase 7, tela 06): lead novo (padrão, sem contato) ou contato existente da conta
// (busca por nome/e-mail/telefone via ContactAPI.search, como o ContactMerge). O escolhido vira o `contact_id` do
// run do playground — o backend nunca aceita contato de outra conta.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { Close16, User16 } from '@carbon/icons-vue';
import ContactAPI from 'dashboard/api/contacts';
import { Input } from 'dashboard/components-next/polaris/ui/input';

const emit = defineEmits(['select']);
const { t } = useI18n();

const mode = ref('new_lead'); // 'new_lead' | 'existing'
const query = ref('');
const results = ref([]);
const isSearching = ref(false);
const selectedContact = ref(null);
const dropdownOpen = ref(false);

const contactLabel = contact =>
  contact.name || contact.email || contact.phone_number || `#${contact.id}`;

const search = debounce(async () => {
  if (!query.value.trim()) {
    results.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const { data } = await ContactAPI.search(query.value.trim());
    results.value = (data?.payload || []).slice(0, 8);
  } catch {
    results.value = [];
  } finally {
    isSearching.value = false;
  }
}, 300);

const onQueryInput = () => {
  dropdownOpen.value = true;
  search();
};

const pick = contact => {
  selectedContact.value = contact;
  dropdownOpen.value = false;
  emit('select', contact.id);
};

const clear = () => {
  selectedContact.value = null;
  query.value = '';
  results.value = [];
  emit('select', null);
};

const selectMode = next => {
  mode.value = next;
  if (next === 'new_lead') clear();
};
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-3 text-sm text-polaris-muted-foreground"
    data-testid="playground-persona-picker"
  >
    <span>{{ t('POLARIS.PLAYGROUND.TESTING_AS.LABEL') }}</span>

    <template v-if="mode === 'existing'">
      <div class="relative">
        <div
          v-if="selectedContact"
          class="flex items-center gap-1.5 rounded-full border border-polaris-border bg-polaris-accent px-3 py-1 text-polaris-foreground"
        >
          <User16 aria-hidden="true" />
          <span class="max-w-56 truncate">{{
            contactLabel(selectedContact)
          }}</span>
          <button
            type="button"
            :aria-label="t('POLARIS.PLAYGROUND.TESTING_AS.CLEAR')"
            @click="clear"
          >
            <Close16 aria-hidden="true" />
          </button>
        </div>
        <template v-else>
          <Input
            v-model="query"
            class="w-72"
            :placeholder="t('POLARIS.PLAYGROUND.TESTING_AS.SEARCH_PLACEHOLDER')"
            @update:model-value="onQueryInput"
            @blur="dropdownOpen = false"
          />
          <ul
            v-if="dropdownOpen && query.trim()"
            class="absolute z-10 mt-1 max-h-64 w-72 overflow-y-auto rounded-md border border-polaris-border bg-polaris-card py-1 shadow-md"
          >
            <li v-if="isSearching" class="px-3 py-2 text-xs">
              {{ t('POLARIS.PLAYGROUND.TESTING_AS.SEARCHING') }}
            </li>
            <li
              v-else-if="!results.length"
              class="px-3 py-2 text-xs text-polaris-muted-foreground"
            >
              {{ t('POLARIS.PLAYGROUND.TESTING_AS.NO_RESULTS') }}
            </li>
            <li v-for="contact in results" :key="contact.id">
              <button
                type="button"
                class="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-polaris-accent"
                @mousedown.prevent="pick(contact)"
              >
                <span class="text-polaris-foreground">{{
                  contactLabel(contact)
                }}</span>
                <span
                  v-if="contact.email || contact.phone_number"
                  class="text-xs text-polaris-muted-foreground"
                >
                  {{
                    [contact.email, contact.phone_number]
                      .filter(Boolean)
                      .join(' · ')
                  }}
                </span>
              </button>
            </li>
          </ul>
        </template>
      </div>
    </template>

    <button
      type="button"
      class="text-polaris-primary hover:underline"
      @click="selectMode(mode === 'existing' ? 'new_lead' : 'existing')"
    >
      {{
        mode === 'existing'
          ? t('POLARIS.PLAYGROUND.TESTING_AS.OR_NEW_LEAD')
          : t('POLARIS.PLAYGROUND.TESTING_AS.OR_EXISTING')
      }}
    </button>
  </div>
</template>
